import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Coins,
    Target,
    TrendingUp,
    Wallet,
    Clock,
    Percent,
    Quote,
    Settings as SettingsIcon,
    ArrowUpRight,
    Sparkles,
    Sliders,
    Briefcase,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";
import PricingGridPanel from "./CockpitPricingGrid";

// ─────────────────────────────────────────────────────────────
// CockpitFinance — dedicated financial cockpit.
// Bannière, quarterly gauges, dossier breakdown, VAT, rentability.
// Settings (motivation + VAT + quarterly targets) editable in place.
// ─────────────────────────────────────────────────────────────

const STATUS_LABELS = {
    draft: "Brouillon",
    audit: "Audit",
    analyse: "Analyse",
    structuration: "Structuration",
    qualification: "Qualification",
    discussion: "Discussions",
    closing: "Closing",
    live: "En cours",
    paused: "Pause",
    closed: "Clos",
};

const EUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
});
const fmtEUR = (n) => EUR.format(Number(n) || 0);

export default function CockpitFinance() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [gridOpen, setGridOpen] = useState(false);

    const load = () =>
        cockpit
            .get("/finance/dashboard")
            .then(({ data }) => setData(data))
            .catch((e) =>
                setErr(e?.response?.data?.detail || "Erreur de chargement"),
            );

    useEffect(() => {
        load();
    }, []);

    if (err)
        return (
            <div className="p-16 text-[#e89090] text-sm">{err}</div>
        );
    if (!data)
        return (
            <div className="p-16 text-[#f4ecd8]/40 text-sm">Chargement…</div>
        );

    const { totals, settings, quarterly, conversion, dossiers, weekly, monthly } = data;

    return (
        <div
            data-testid="cockpit-finance"
            className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14"
        >
            {/* HEADER */}
            <div className="flex flex-wrap items-end justify-between mb-10 gap-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Cockpit financier · Confidentiel
                    </div>
                    <h1
                        className="font-serif text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.08] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Pilotage des honoraires.
                    </h1>
                    {settings.motivation_phrase && (
                        <p
                            data-testid="motivation-phrase"
                            className="mt-4 font-serif text-[16px] sm:text-[18px] italic text-[#d4af37]/85 max-w-2xl leading-relaxed"
                            style={{ fontWeight: 400 }}
                        >
                            <Quote
                                strokeWidth={1.1}
                                size={14}
                                className="inline-block mr-2 -mt-1 text-[#d4af37]/60"
                            />
                            {settings.motivation_phrase}
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        data-testid="finance-settings-toggle"
                        onClick={() => setSettingsOpen((v) => !v)}
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-white/20 text-[#f4ecd8] text-[11px] tracking-[0.3em] uppercase hover:bg-white/[0.04] transition-all duration-500"
                    >
                        <SettingsIcon strokeWidth={1.2} size={13} />
                        Paramètres
                    </button>
                    <button
                        type="button"
                        data-testid="finance-pricing-toggle"
                        onClick={() => setGridOpen((v) => !v)}
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Sliders strokeWidth={1.2} size={13} />
                        Grille Tarifaire XV
                    </button>
                </div>
            </div>

            {settingsOpen && (
                <FinanceSettingsForm
                    initial={{
                        motivation_phrase: settings.motivation_phrase || "",
                        default_vat_rate: settings.default_vat_rate || 20,
                        target_weekly: settings.target_weekly || 0,
                        target_monthly: settings.target_monthly || 0,
                        target_q1: quarterly.targets.q1 || 0,
                        target_q2: quarterly.targets.q2 || 0,
                        target_q3: quarterly.targets.q3 || 0,
                        target_q4: quarterly.targets.q4 || 0,
                    }}
                    onSaved={() => {
                        setSettingsOpen(false);
                        load();
                    }}
                />
            )}

            {gridOpen && (
                <PricingGridPanel onClose={() => setGridOpen(false)} />
            )}

            {/* TOP BANNER */}
            <section
                data-testid="finance-banner"
                className="mb-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#d4af37]/15 border border-[#d4af37]/30"
            >
                <BannerCell
                    icon={Coins}
                    label="CA réalisé"
                    value={fmtEUR(totals.revenue_realized)}
                    sub="Factures encaissées"
                />
                <BannerCell
                    icon={TrendingUp}
                    label="CA engagé"
                    value={fmtEUR(totals.revenue_pending)}
                />
                <BannerCell
                    icon={Target}
                    label="CA cible cumulé"
                    value={fmtEUR(totals.revenue_target_total)}
                    sub="Somme des cibles dossiers actifs"
                />
                <BannerCell
                    icon={Wallet}
                    label="Reste à facturer"
                    value={fmtEUR(totals.remaining_to_invoice)}
                />
            </section>

            {/* WEEKLY + MONTHLY — motivating rhythm view */}
            <section className="mb-12 grid lg:grid-cols-5 gap-6">
                <WeeklyCard weekly={weekly} />
                <MonthlyCard monthly={monthly} />
            </section>

            {/* QUARTERLY GAUGES */}
            <section
                data-testid="finance-quarterly"
                className="mb-12 bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10"
            >
                <div className="flex items-baseline justify-between mb-7 gap-4 flex-wrap">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-2">
                            Objectifs {quarterly.year}
                        </div>
                        <h2
                            className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                            style={{ fontWeight: 400 }}
                        >
                            Progression annuelle
                        </h2>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/65 mb-1">
                            Cumul annuel
                        </div>
                        <div
                            className="font-serif text-[22px] text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            {fmtEUR(quarterly.annual_realized)}
                            <span className="text-[#f4ecd8]/40 text-[14px] ml-2">
                                / {fmtEUR(quarterly.annual_target)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {["q1", "q2", "q3", "q4"].map((q) => (
                        <QuarterGauge
                            key={q}
                            label={q.toUpperCase()}
                            realized={quarterly.realized[q]}
                            target={quarterly.targets[q]}
                        />
                    ))}
                </div>
            </section>

            {/* CONVERSION + VAT */}
            <section className="mb-12 grid lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
                <ConversionCell conversion={conversion} />
                <InfoCell
                    icon={Percent}
                    label="TVA collectée (réalisée)"
                    value={fmtEUR(totals.vat_realized)}
                    sub={`Taux par défaut : ${settings.default_vat_rate}%`}
                />
                <InfoCell
                    icon={Percent}
                    label="TVA en attente"
                    value={fmtEUR(totals.vat_pending)}
                    sub="Sur factures non encaissées"
                />
            </section>

            {/* PROSPECTS À TRANSFORMER */}
            <PendingProspectsCard
                items={conversion.pending_prospects || []}
            />

            {/* PIPELINE HONORAIRES XV */}
            <PipelineHonorairesCard pipeline={data.pipeline} />

            {/* DOSSIER BREAKDOWN */}
            <section data-testid="finance-dossiers" className="mb-12">
                <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-2">
                            Pipeline financier
                        </div>
                        <h2
                            className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                            style={{ fontWeight: 400 }}
                        >
                            Détail par dossier
                        </h2>
                    </div>
                    <p className="text-[11px] text-[#f4ecd8]/40 italic font-light max-w-md leading-relaxed">
                        Cliquez sur un dossier pour ouvrir sa fiche complète et
                        ajuster cible, TVA ou temps estimé.
                    </p>
                </div>
                <div className="border border-white/[0.06]">
                    <div className="hidden lg:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-4 px-4 py-3 border-b border-white/[0.06] bg-white/[0.02] text-[9.5px] tracking-[0.24em] uppercase text-[#d4af37]/60">
                        <span>Dossier</span>
                        <span>Statut</span>
                        <span className="text-right">Cible</span>
                        <span className="text-right">Réalisé</span>
                        <span className="text-right">Engagé</span>
                        <span className="text-right">€/h</span>
                        <span />
                    </div>
                    {dossiers.length === 0 ? (
                        <p className="text-[12px] text-[#f4ecd8]/35 italic font-light py-10 text-center">
                            Aucun dossier — créez votre premier dossier.
                        </p>
                    ) : (
                        dossiers.map((d) => (
                            <DossierFinanceRow key={d.id} d={d} />
                        ))
                    )}
                </div>
            </section>
        </div>
    );
}

function BannerCell({ icon: Icon, label, value, sub }) {
    return (
        <div className="bg-[#0b1220] p-8 lg:p-9">
            <div className="overline text-[#d4af37]/85 mb-3 flex items-center gap-2">
                {Icon && <Icon strokeWidth={1.1} size={12} />}
                {label}
            </div>
            <div
                className="font-serif text-[32px] sm:text-[38px] text-[#f4ecd8] leading-none"
                style={{ fontWeight: 300 }}
            >
                {value}
            </div>
            {sub && (
                <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55">
                    {sub}
                </div>
            )}
        </div>
    );
}

function QuarterGauge({ label, realized, target }) {
    const pct = target > 0 ? Math.min(100, (realized / target) * 100) : 0;
    return (
        <div
            data-testid={`quarter-${label.toLowerCase()}`}
            className="bg-[#070d18] p-5"
        >
            <div className="flex items-baseline justify-between mb-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/75">
                    {label}
                </span>
                <span className="text-[11px] text-[#f4ecd8]/55">
                    {Math.round(pct)}%
                </span>
            </div>
            <div className="h-[3px] bg-white/[0.06] mb-3">
                <div
                    className="h-full bg-[#d4af37]/85"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="font-serif text-[16px] text-[#f4ecd8] tabular-nums">
                {fmtEUR(realized)}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/40 tabular-nums">
                / {fmtEUR(target)}
            </div>
        </div>
    );
}

function ConversionCell({ conversion }) {
    return (
        <div
            data-testid="conversion-cell"
            className="bg-[#0b1220] p-6 border-l-2 border-[#d4af37]"
        >
            <div className="overline text-[#d4af37]/85 mb-2 flex items-center gap-2">
                <Sparkles strokeWidth={1.1} size={11} />
                Taux de transformation
            </div>
            <div
                className="font-serif text-[36px] text-[#d4af37] leading-none"
                style={{ fontWeight: 300 }}
            >
                {conversion.rate}%
            </div>
            <div className="mt-3 text-[11px] text-[#f4ecd8]/55">
                {conversion.clients} client{conversion.clients > 1 ? "s" : ""}
                <span className="text-[#f4ecd8]/30 mx-2">·</span>
                {conversion.prospects} prospect
                {conversion.prospects > 1 ? "s" : ""} à transformer
            </div>
        </div>
    );
}

function InfoCell({ icon: Icon, label, value, sub }) {
    return (
        <div className="bg-[#0b1220] p-6">
            <div className="overline text-[#d4af37]/75 mb-2 flex items-center gap-2">
                {Icon && <Icon strokeWidth={1.1} size={11} />}
                {label}
            </div>
            <div
                className="font-serif text-[26px] text-[#f4ecd8] leading-none"
                style={{ fontWeight: 300 }}
            >
                {value}
            </div>
            {sub && (
                <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/45">
                    {sub}
                </div>
            )}
        </div>
    );
}

function DossierFinanceRow({ d }) {
    const pct = d.revenue_target > 0
        ? Math.min(100, (d.revenue_realized / d.revenue_target) * 100)
        : 0;
    return (
        <Link
            to={`/cockpit/dossiers/${d.id}`}
            data-testid={`finance-row-${d.id}`}
            className={`block grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-4 px-4 py-4 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] transition-colors duration-500 ${
                d.is_archived ? "opacity-50" : ""
            }`}
        >
            <div className="min-w-0">
                <div
                    className="font-serif text-[15px] text-[#f4ecd8] leading-tight truncate"
                    style={{ fontWeight: 400 }}
                >
                    {d.name}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase">
                    <Clock strokeWidth={1.1} size={9} className="text-[#d4af37]/60" />
                    <span className="text-[#f4ecd8]/45">
                        {d.hours_spent}h sur {d.estimated_hours || "—"}h
                        {d.hours_remaining > 0 && (
                            <span className="text-[#d4af37]/70 ml-2">
                                · {d.hours_remaining}h restantes
                            </span>
                        )}
                    </span>
                </div>
            </div>
            <div>
                <span
                    className={`text-[10px] tracking-[0.22em] uppercase ${
                        d.is_client
                            ? "text-[#d4af37]"
                            : d.is_prospect
                              ? "text-[#9bb4d6]"
                              : "text-[#f4ecd8]/45"
                    }`}
                >
                    {STATUS_LABELS[d.status] || d.status}
                </span>
            </div>
            <div className="text-right">
                <div className="text-[14px] font-serif text-[#f4ecd8]/85 tabular-nums">
                    {fmtEUR(d.revenue_target)}
                </div>
            </div>
            <div className="text-right">
                <div className="text-[14px] font-serif text-[#f4ecd8] tabular-nums">
                    {fmtEUR(d.revenue_realized)}
                </div>
                {d.revenue_target > 0 && (
                    <div className="mt-1 h-[2px] bg-white/[0.06]">
                        <div
                            className="h-full bg-[#d4af37]/80"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                )}
            </div>
            <div className="text-right">
                <div className="text-[14px] font-serif text-[#d4af37]/85 tabular-nums">
                    {fmtEUR(d.revenue_pending)}
                </div>
                {d.vat_exempt ? (
                    <div className="mt-1 text-[9.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/45">
                        Exonéré TVA
                    </div>
                ) : (
                    <div className="mt-1 text-[9.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/45">
                        TVA {d.vat_rate}% · {fmtEUR(d.vat_pending)}
                    </div>
                )}
            </div>
            <div className="text-right">
                <div className="text-[13px] font-serif text-[#f4ecd8]/80 tabular-nums">
                    {d.hourly_rate ? `${fmtEUR(d.hourly_rate)}/h` : "—"}
                </div>
            </div>
            <div className="flex items-center justify-end">
                <ArrowUpRight
                    strokeWidth={1.1}
                    size={14}
                    className="text-[#d4af37]/60"
                />
            </div>
        </Link>
    );
}

function FinanceSettingsForm({ initial, onSaved }) {
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const change = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await cockpit.put("/settings/finance", {
                motivation_phrase: form.motivation_phrase,
                default_vat_rate: Number(form.default_vat_rate) || 0,
                target_weekly: Number(form.target_weekly) || 0,
                target_monthly: Number(form.target_monthly) || 0,
                target_q1: Number(form.target_q1) || 0,
                target_q2: Number(form.target_q2) || 0,
                target_q3: Number(form.target_q3) || 0,
                target_q4: Number(form.target_q4) || 0,
            });
            onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="finance-settings-form"
            className="mb-12 bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10 space-y-6"
        >
            <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                    Phrase de motivation
                </label>
                <input
                    type="text"
                    data-testid="motivation-input"
                    value={form.motivation_phrase}
                    onChange={change("motivation_phrase")}
                    placeholder="Discipline patiente, valeur durable."
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[16px] font-serif italic text-[#f4ecd8]"
                />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                    <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                        TVA défaut (%)
                    </label>
                    <input
                        type="number"
                        min={0}
                        max={50}
                        step={0.5}
                        value={form.default_vat_rate}
                        onChange={change("default_vat_rate")}
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8]"
                    />
                </div>
                <div>
                    <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                        Objectif hebdo (€)
                    </label>
                    <input
                        type="number"
                        min={0}
                        data-testid="target-weekly-input"
                        value={form.target_weekly}
                        onChange={change("target_weekly")}
                        placeholder="auto = annuel / 52"
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                    />
                </div>
                <div>
                    <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                        Objectif mensuel (€)
                    </label>
                    <input
                        type="number"
                        min={0}
                        data-testid="target-monthly-input"
                        value={form.target_monthly}
                        onChange={change("target_monthly")}
                        placeholder="auto = annuel / 12"
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                    />
                </div>
                <div />
                {["q1", "q2", "q3", "q4"].map((q) => (
                    <div key={q}>
                        <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                            Cible {q.toUpperCase()} (€)
                        </label>
                        <input
                            type="number"
                            min={0}
                            value={form[`target_${q}`]}
                            onChange={change(`target_${q}`)}
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8]"
                        />
                    </div>
                ))}
            </div>
            {err && (
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                    {err}
                </div>
            )}
            <div className="flex justify-end">
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="finance-settings-save"
                    className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    {busy ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>
        </form>
    );
}

// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// WeeklyCard — current/past weeks with navigation + payments list
// ─────────────────────────────────────────────────────────────
function WeeklyCard({ weekly }) {
    return (
        <BreakdownCard
            period="week"
            initialOffset={0}
            fallbackTitle={`Semaine en cours · du ${weekly?.monday} au ${weekly?.sunday}`}
            kicker="Encaissés cette semaine"
            tone="primary"
        />
    );
}

// ─────────────────────────────────────────────────────────────
// MonthlyCard — current/past months with navigation + payments
// ─────────────────────────────────────────────────────────────
function MonthlyCard({ monthly }) {
    const MONTHS_FR = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
    ];
    const monthName = MONTHS_FR[(monthly?.month || 1) - 1];
    return (
        <BreakdownCard
            period="month"
            initialOffset={0}
            fallbackTitle={`Bilan mensuel · ${monthName} ${monthly?.year || ""}`}
            kicker="Encaissés ce mois"
            tone="muted"
            seriesPrev={monthly?.prev_realized}
        />
    );
}

// ─────────────────────────────────────────────────────────────
// BreakdownCard — shared component for weekly + monthly breakdown
// with prev/next navigation + payments list + AI pilotage.
// ─────────────────────────────────────────────────────────────
function BreakdownCard({
    period,
    initialOffset = 0,
    fallbackTitle,
    kicker,
    tone = "muted",
    seriesPrev = null,
}) {
    const [offset, setOffset] = useState(initialOffset);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        setBusy(true);
        setErr("");
        cockpit
            .get(`/finance/breakdown?period=${period}&offset=${offset}`)
            .then(({ data }) => {
                if (alive) setData(data);
            })
            .catch((e) => {
                if (alive)
                    setErr(
                        e?.response?.data?.detail
                        || "Période indisponible — réessayez.",
                    );
            })
            .finally(() => {
                if (alive) setBusy(false);
            });
        return () => {
            alive = false;
        };
    }, [period, offset]);

    const pct = data?.target > 0
        ? Math.min(100, Math.round((data.realized / data.target) * 100))
        : 0;
    const ahead = data?.target > 0 && data.realized >= data.target;

    const heading = data?.label
        ? (period === "week"
              ? `${offset === 0 ? "Semaine en cours" : "Semaine"} · ${data.label}`
              : `Bilan mensuel · ${data.label}`)
        : fallbackTitle;

    const isPrimary = tone === "primary";
    const span = isPrimary ? "lg:col-span-2" : "lg:col-span-3";

    return (
        <div
            data-testid={`${period}-card`}
            className={`${span} ${
                isPrimary
                    ? "bg-[#070d18] border border-[#d4af37]/25"
                    : "bg-white/[0.02] border border-white/[0.06]"
            } p-8 lg:p-10`}
        >
            {/* Header with navigation */}
            <div className="flex items-baseline justify-between gap-3 mb-6 flex-wrap">
                <div
                    className={`overline ${
                        isPrimary ? "text-[#d4af37]/85" : "text-[#d4af37]/70"
                    }`}
                >
                    {heading}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid={`${period}-nav-prev`}
                        disabled={busy}
                        onClick={() => setOffset((o) => o + 1)}
                        aria-label="Période précédente"
                        className="w-7 h-7 inline-flex items-center justify-center border border-white/15 hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-500 text-[#f4ecd8]/55 disabled:opacity-30"
                    >
                        <ChevronLeft strokeWidth={1.4} size={13} />
                    </button>
                    <button
                        type="button"
                        data-testid={`${period}-nav-next`}
                        disabled={busy || offset === 0}
                        onClick={() => setOffset((o) => Math.max(0, o - 1))}
                        aria-label="Période suivante"
                        className="w-7 h-7 inline-flex items-center justify-center border border-white/15 hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-500 text-[#f4ecd8]/55 disabled:opacity-30"
                    >
                        <ChevronRight strokeWidth={1.4} size={13} />
                    </button>
                    {offset > 0 && (
                        <button
                            type="button"
                            data-testid={`${period}-nav-today`}
                            onClick={() => setOffset(0)}
                            className="ml-1 text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65 hover:text-[#d4af37] transition-colors duration-500"
                        >
                            {period === "week" ? "Cette semaine" : "Ce mois"}
                        </button>
                    )}
                </div>
            </div>

            {/* Totals + progress */}
            <div className="flex items-end justify-between gap-6 mb-4 flex-wrap">
                <div>
                    <div
                        className={`font-serif leading-none ${
                            isPrimary
                                ? "text-[44px] sm:text-[54px] text-[#f4ecd8]"
                                : "text-[36px] sm:text-[42px] text-[#f4ecd8]"
                        }`}
                        style={{ fontWeight: 300 }}
                    >
                        {fmtEUR(data?.realized || 0)}
                    </div>
                    <div className="mt-2 text-[11px] tracking-[0.26em] uppercase text-[#f4ecd8]/45">
                        {kicker}
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-serif text-[20px] text-[#d4af37]">
                        {fmtEUR(data?.target || 0)}
                    </div>
                    <div className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/45 mt-1">
                        Objectif {period === "week" ? "hebdo" : "mensuel"}
                    </div>
                </div>
            </div>
            <div className="h-[5px] bg-white/[0.06] mb-6">
                <div
                    className={`h-full ${ahead ? "bg-[#d4af37]" : "bg-[#d4af37]/70"} transition-all duration-700`}
                    style={{ width: `${pct}%` }}
                />
            </div>

            {/* Payments list */}
            {data?.invoices && data.invoices.length > 0 && (
                <div className="mb-6">
                    <div className="overline text-[#d4af37]/65 mb-3">
                        Encaissements
                        <span className="ml-2 text-[10px] text-[#f4ecd8]/45 normal-case tracking-normal">
                            ({data.invoices.length})
                        </span>
                    </div>
                    <ul
                        data-testid={`${period}-payments`}
                        className="divide-y divide-white/[0.05] border-t border-white/[0.05] border-b"
                    >
                        {data.invoices.slice(0, 6).map((inv, i) => (
                            <li
                                key={`${inv.paid_at}-${i}`}
                                data-testid={`${period}-payment-${i}`}
                                className="flex items-center justify-between gap-4 py-3"
                            >
                                <div className="min-w-0">
                                    <div
                                        className="font-serif text-[14.5px] text-[#f4ecd8] truncate"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {inv.dossier_name}
                                    </div>
                                    <div className="text-[10.5px] tracking-[0.18em] uppercase text-[#f4ecd8]/40 mt-0.5">
                                        {inv.label || "—"}
                                        {" · "}
                                        {new Date(inv.paid_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                                    </div>
                                </div>
                                <div className="font-serif text-[15px] text-[#d4af37] tabular-nums shrink-0">
                                    {fmtEUR(inv.amount)}
                                </div>
                            </li>
                        ))}
                    </ul>
                    {data.invoices.length > 6 && (
                        <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/40">
                            +{data.invoices.length - 6} autre{data.invoices.length - 6 > 1 ? "s" : ""}
                        </div>
                    )}
                </div>
            )}
            {data?.invoices && data.invoices.length === 0 && !busy && (
                <p className="mb-6 text-[12.5px] text-[#f4ecd8]/40 italic font-light">
                    Aucun encaissement sur cette période.
                </p>
            )}
            {busy && (
                <p className="mb-6 text-[12.5px] text-[#f4ecd8]/40 italic font-light">
                    Chargement…
                </p>
            )}

            {/* AI pilotage */}
            {data?.pilotage && (
                <div
                    data-testid={`${period}-pilotage`}
                    className="border-l-2 border-[#d4af37]/40 pl-4 py-1"
                >
                    <div className="overline text-[#d4af37]/65 mb-2 flex items-center gap-2">
                        <Sparkles strokeWidth={1.2} size={11} />
                        Suggestion de pilotage IA
                    </div>
                    <p className="font-serif text-[13.5px] italic text-[#f4ecd8]/85 leading-relaxed">
                        « {data.pilotage} »
                    </p>
                </div>
            )}

            {err && (
                <div className="text-[12.5px] text-[#e89090] border-l-2 border-[#e89090] pl-3 py-1 mt-3">
                    {err}
                </div>
            )}

            {/* Optional: comparison with previous (monthly only, offset 0) */}
            {period === "month" && offset === 0 && seriesPrev != null && (
                <div className="mt-5 pt-5 border-t border-white/[0.06] text-[11px] text-[#f4ecd8]/55 tracking-wider">
                    Mois précédent : {fmtEUR(seriesPrev)}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// PendingProspectsCard — list prospects to transform
// ─────────────────────────────────────────────────────────────
function PendingProspectsCard({ items }) {
    if (!items || items.length === 0) return null;
    const STATUS_LABELS = {
        draft: "Brouillon",
        audit: "Audit",
        analyse: "Analyse",
    };
    return (
        <section
            data-testid="pending-prospects"
            className="mb-12 bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10"
        >
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-2">
                        Prospects à transformer · {items.length}
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Pipeline d'engagement
                    </h2>
                </div>
                <p className="text-[11px] text-[#f4ecd8]/40 italic font-light max-w-md leading-relaxed">
                    Cliquez sur un prospect pour ouvrir sa fiche et générer
                    une suggestion d'accompagnement par IA (privée).
                </p>
            </div>
            <ul className="divide-y divide-white/[0.05]">
                {items.map((p) => (
                    <li
                        key={p.id}
                        data-testid={`pending-prospect-${p.id}`}
                    >
                        <Link
                            to={`/cockpit/dossiers/${p.id}`}
                            className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-5 py-3.5 px-2 hover:bg-white/[0.02] transition-colors duration-500"
                        >
                            <div className="min-w-0">
                                <div
                                    className="font-serif text-[15px] text-[#f4ecd8] leading-tight truncate"
                                    style={{ fontWeight: 400 }}
                                >
                                    {p.name}
                                </div>
                                <div className="mt-0.5 text-[10.5px] tracking-[0.22em] uppercase text-[#d4af37]/70">
                                    {STATUS_LABELS[p.status] || p.status}
                                </div>
                            </div>
                            <span className="text-[11px] font-serif text-[#f4ecd8]/55 tabular-nums">
                                {p.revenue_target > 0
                                    ? fmtEUR(p.revenue_target)
                                    : "—"}
                            </span>
                            <span className="text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/40">
                                {p.days_in_status !== null
                                    ? `${p.days_in_status} j`
                                    : "—"}
                            </span>
                            {p.has_ai_suggestion ? (
                                <span className="text-[9.5px] tracking-[0.22em] uppercase text-[#d4af37]">
                                    ⚐ IA prête
                                </span>
                            ) : (
                                <span className="text-[9.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/30">
                                    Sans IA
                                </span>
                            )}
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// PipelineHonorairesCard — XV pricing pipeline (7 KPIs)
// ─────────────────────────────────────────────────────────────
function PipelineHonorairesCard({ pipeline }) {
    if (!pipeline) return null;
    const items = [
        { key: "fees_recommended", label: "Honoraires recommandés", value: pipeline.fees_recommended, tone: "muted" },
        { key: "fees_retained", label: "Honoraires retenus", value: pipeline.fees_retained, tone: "gold" },
        { key: "fees_invoiced", label: "Honoraires facturés", value: pipeline.fees_invoiced, tone: "muted" },
        { key: "fees_collected", label: "Honoraires encaissés", value: pipeline.fees_collected, tone: "primary" },
        { key: "success_fees_potential", label: "Success fees potentiels", value: pipeline.success_fees_potential, tone: "muted" },
        { key: "success_fees_secured", label: "Success fees sécurisés", value: pipeline.success_fees_secured, tone: "gold" },
        { key: "success_fees_collected", label: "Success fees encaissés", value: pipeline.success_fees_collected, tone: "primary" },
    ];
    return (
        <section
            data-testid="pipeline-honoraires"
            className="mb-12 bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10"
        >
            <div className="flex items-baseline justify-between mb-7 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-2 flex items-center gap-2">
                        <Briefcase strokeWidth={1.1} size={11} />
                        Pipeline Honoraires XV
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Cumul des valorisations actives
                    </h2>
                </div>
                <p className="text-[11px] text-[#f4ecd8]/40 italic font-light max-w-md leading-relaxed">
                    Agrégation des recommandations IA et décisions cabinet sur
                    l'ensemble des dossiers non archivés.
                </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.05]">
                {items.map((it) => (
                    <div
                        key={it.key}
                        data-testid={`pipeline-${it.key}`}
                        className="bg-[#070d18] p-6"
                    >
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/60 mb-3">
                            {it.label}
                        </div>
                        <div
                            className={`font-serif text-[22px] sm:text-[24px] leading-none tabular-nums ${
                                it.tone === "primary"
                                    ? "text-[#d4af37]"
                                    : it.tone === "gold"
                                      ? "text-[#f4ecd8]"
                                      : "text-[#f4ecd8]/70"
                            }`}
                            style={{ fontWeight: 400 }}
                        >
                            {fmtEUR(it.value)}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}


